import { ArrowLeftIcon, ShareIcon } from "@heroicons/react/24/outline";
import FaqAccordion from "component/Common/FaqAccordion";
import ShareModal from "component/Common/ShareModal";
import { renderTitleDescription } from "component/Landing/Home/helper";
import { dateFormat, getDownloadAppLink, history } from "helpers";
import { useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom/cjs/react-router-dom.min";
import { publicEndPoints } from "service/endpoints";
import { request } from "service/request";
import EditorParser from "./editor";
import { CircularSpinner } from "helpers/muiComponents";
import { Helmet } from "react-helmet-async";
import { landingImages } from "helpers/images";

const BlogsDetailsComponent = () => {
  const location = useLocation();
  const blogSlug = useParams();
  const [showShareModal, setShowShareModal] = useState(false);
  const [blogData, setBlogData] = useState({});
  const [relatedBlog, setRelatedBlog] = useState([]);
  const [inviteText, setInviteText] = useState();
  const [isBlogApiLoading, setIsBlogApiLoading] = useState(true);

  const getDetails = async () => {
    try {
      const response = await request({
        url: `${publicEndPoints.blogs.baseUrl}/${blogSlug?.slug}`,
        showToast: false,
        location: location,
        isNotOpen: false,
      });
      if (response?.data) {
        setBlogData(response?.data || null);
        setIsBlogApiLoading(false);
      }
    } catch (error) {
      setIsBlogApiLoading(false);
      console.error("Error fetching data:", error);
    }
  };
  const getRelatedBlogs = async () => {
    try {
      const response = await request({
        url: `${publicEndPoints.blogs.baseUrl}/${blogSlug?.slug}/related_blogs`,
        showToast: false,
        location: location,
        isNotOpen: false,
      });
      if (response?.data) {
        setRelatedBlog(response?.data?.related_blogs || null);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  useEffect(() => {
    getDetails();
    getRelatedBlogs();
  }, [blogSlug]);

  const EditorJsViewer = ({ data }) => {
    if (!data) {
      return <p>No content to display</p>;
    }
    return <EditorParser data={data} />;
  };
  const currentUrl = window.location.href;

  return (
    <>
      {blogData?.meta_title && blogData?.meta_description && (
        <Helmet>
          <title>{blogData?.meta_title}</title>
          <meta name="description" content={blogData?.meta_description} />
          <link rel="canonical" href={currentUrl} />

          <meta property="og:title" content={blogData?.meta_title} />
          <meta property="og:description" content={blogData?.meta_description} />
          <meta property="og:type" content="article" />
          <meta property="og:image" content={blogData?.thumbnail_url} />
          <meta property="og:url" content={currentUrl} />

          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={blogData?.meta_title} />
          <meta name="twitter:description" content={blogData?.meta_description} />
          <meta name="twitter:image" content={blogData?.thumbnail_url} />
        </Helmet>
      )}
      <section className="flex flex-col items-center justify-center w-full ">
        <div className="flex flex-col w-full max-w-screen-lg py-4">
          <div className="flex flex-col">
            <div className="flex justify-between w-full pr-4 pl-1">
              <div
                className="flex items-center gap-2 py-2 pr-4 pl-3 text-grey-700 hover:bg-grey-50 rounded-full cursor-pointer"
                onClick={() => history.push("/blogs")}
              >
                <ArrowLeftIcon className="w-4" />
                <p className="md:text-sm text-xs font-medium">All blogs</p>
              </div>
              <div
                onClick={() => {
                  setShowShareModal(true);
                  setInviteText(currentUrl);
                }}
                className="flex gap-2 !py-2 !pr-4 !pl-3 rounded-full border-card-width border-grey-border hover:bg-grey-50 cursor-pointer"
              >
                <ShareIcon className="w-4 text-grey-700" />
                <p className="hidden md:flex text-sm font-medium">Share</p>
              </div>
            </div>
            <div className="p-4">
              <p className="w-full md:text-base text-xs font-medium text-secondary">
                {blogData?.created_at}
              </p>
              <h1 className="font-bold text-xl landing-sm:text-2xl">{blogData?.title}</h1>
            </div>
          </div>
          <div className="flex gap-6 w-full h-full">
            <div className="w-full">
              {blogData?.thumbnail_url && blogData?.show_thumbnail && (
                <img src={blogData?.thumbnail_url} alt="blog" className="w-full mb-4 rounded-lg" />
              )}
              {isBlogApiLoading ? (
                <div className="w-full flex justify-center">
                  <CircularSpinner />
                </div>
              ) : blogData?.content ? (
                <EditorJsViewer data={blogData.content} />
              ) : (
                <div className="flex justify-center items-center h-80 text-xl font-medium text-center">
                  No blog found
                </div>
              )}
            </div>

            {blogData?.content && (
              <div className="sticky top-20 hidden md:block w-72 lg:w-[360px] h-full pr-4">
                <a href="/portal/dashboard" target="_blank" rel="noopener noreferrer">
                  <img
                    src={landingImages?.blogWebBanner}
                    alt="banner"
                    className="w-full h-auto max-h-[960px] object-cover rounded-lg"
                  />
                </a>
              </div>
            )}
          </div>
        </div>

        {blogData?.faqs?.length > 0 && (
          <div className="flex flex-col items-center gap-[clamp(24px,3vw,40px)] py-10 w-full">
            {renderTitleDescription(
              "Frequently Asked Questions",
              "Here are some answers to questions you might be looking for."
            )}
            <div className="w-full max-w-[720px]">
              <FaqAccordion accordionItems={blogData?.faqs} />
            </div>
          </div>
        )}
        {relatedBlog.length > 0 && (
          <div className="flex flex-col gap-[clamp(16px,3vw,24px)] w-full md:my-10">
            {renderTitleDescription("Related Blogs")}
            <div className="flex gap-2 px-[clamp(16px,4.5vw,64px)] py-2 w-full scrollbar-hover">
              {relatedBlog?.map((item, index) => (
                <a
                  href={`/blogs/${item?.slug}`}
                  target="_self"
                  key={index}
                  className="w-80 bg-white rounded-2xl shadow-[#0000003D_0px_4px_64px_-24px] flex-shrink-0 cursor-pointer"
                >
                  <img
                    src={item?.thumbnail_url}
                    alt=""
                    className="w-full aspect-video object-cover rounded-t-2xl"
                  />
                  <div className="flex flex-col gap-3 p-4 max-w-full w-full">
                    <div className="space-y-2">
                      <p className="text-base font-bold line-clamp-2 text-ellipsis">
                        {item?.title}
                      </p>
                      <p className="text-xs font-medium line-clamp-2 text-ellipsis">
                        {item?.summary}
                      </p>
                    </div>
                    <div className="flex justify-between w-full px-1">
                      {item?.created_at && (
                        <p className="font-medium text-xs">{dateFormat(item?.created_at)}</p>
                      )}
                      <div
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setShowShareModal(true);
                          setInviteText(
                            `${window.location.protocol}//${window.location.host}/blogs/${item?.slug}`
                          );
                        }}
                        className="p-2 rounded-full hover:bg-grey-200 cursor-pointer"
                      >
                        <ShareIcon className="w-4 text-grey-700" />
                      </div>
                    </div>
                  </div>
                </a>
              ))}
            </div>
          </div>
        )}
        {blogData?.content && (
          <div className="sticky top-20 md:hidden flex justify-center w-full h-full pt-6 px-4">
            <a
              href={getDownloadAppLink("/portal/dashboard")}
              target="_blank"
              rel="noopener noreferrer"
              className="cursor-pointer"
            >
              <img
                src={landingImages?.blogMobBanner}
                alt="banner"
                className="max-h-[440px] object-contain rounded-lg"
              />
            </a>
          </div>
        )}

        {showShareModal && (
          <ShareModal
            inviteText={inviteText}
            setShowShareModal={setShowShareModal}
            showShareModal={showShareModal}
          />
        )}
      </section>
    </>
  );
};

export default BlogsDetailsComponent;
